import React, { useState, useRef, useEffect } from 'react';
import { Clock, Users, Paperclip, Star, X, Check, DollarSign, ChevronDown, ChevronUp, MessageSquare, Phone, Upload, FileText, Loader2, Globe, Mail, MapPin, RefreshCw, Send } from 'lucide-react';
import { requestsService } from '../../services/requestsService';

const UserDashboard = ({ bookId, requests, onUpdateRequest, onAcceptOffer, user }) => {
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isManageModalOpen, setIsManageModalOpen] = useState(false);
  const [requestToCancel, setRequestToCancel] = useState(null);
  
  // Offers state per request
  const [requestOffers, setRequestOffers] = useState({});
  const [loadingOffers, setLoadingOffers] = useState({});
  const [expandedOffers, setExpandedOffers] = useState({});
  const [externalLeads, setExternalLeads] = useState({});
  const [discoveryMeta, setDiscoveryMeta] = useState({});
  const [discoveringRequests, setDiscoveringRequests] = useState({});
  const [dispatchingRequests, setDispatchingRequests] = useState({});
  
  // Feedback State
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  
  // File Upload State
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const autoDiscoveryRef = useRef({});

  // Fetch offers for all OPEN requests on mount
  useEffect(() => {
    if (!bookId || !requests?.length) return;
    
    const openRequests = requests.filter(r => r.status === 'OPEN' || r.status === 'open' || r.status === 'submitted');
    openRequests.forEach(req => {
      fetchOffersForRequest(req.id, req);
      if (req.externalDiscovery?.status && req.externalDiscovery.status !== 'idle') {
        fetchExternalLeads(req.id);
      }
    });
  }, [bookId, requests]);

  useEffect(() => {
    if (!requests?.length) return;

    setDiscoveryMeta((prev) => {
      const next = { ...prev };
      requests.forEach((req) => {
        if (req.externalDiscovery) {
          next[req.id] = req.externalDiscovery;
        }
      });
      return next;
    });
  }, [requests]);


  const fetchExternalLeads = async (requestId) => {
    if (!bookId || !requestId) return;

    try {
      const leads = await requestsService.getExternalProviderLeads(bookId, requestId);
      setExternalLeads((prev) => ({ ...prev, [requestId]: leads }));
    } catch (error) {
      console.error('Failed to fetch external provider leads:', error);
    }
  };

  const triggerExternalDiscovery = async (requestRecord, force = false) => {
    if (!bookId || !requestRecord?.id || !user?.uid) return;
    if (discoveringRequests[requestRecord.id]) return;

    setDiscoveringRequests((prev) => ({ ...prev, [requestRecord.id]: true }));
    setDiscoveryMeta((prev) => ({
      ...prev,
      [requestRecord.id]: {
        ...(prev[requestRecord.id] || requestRecord.externalDiscovery || {}),
        status: 'searching'
      }
    }));

    try {
      const result = await requestsService.discoverExternalProvidersForRequest(bookId, requestRecord.id, force);
      if (result?.discovery) {
        setDiscoveryMeta((prev) => ({ ...prev, [requestRecord.id]: result.discovery }));
      }
      if (Array.isArray(result?.leads)) {
        setExternalLeads((prev) => ({ ...prev, [requestRecord.id]: result.leads }));
      } else {
        await fetchExternalLeads(requestRecord.id);
      }
    } catch (error) {
      console.error('Failed to discover external providers:', error);
      setDiscoveryMeta((prev) => ({
        ...prev,
        [requestRecord.id]: {
          ...(prev[requestRecord.id] || requestRecord.externalDiscovery || {}),
          status: 'failed',
          error: error?.message || 'External discovery failed'
        }
      }));
    } finally {
      autoDiscoveryRef.current[requestRecord.id] = true;
      setDiscoveringRequests((prev) => ({ ...prev, [requestRecord.id]: false }));
    }
  };

  const dispatchOutboundRequests = async (requestRecord) => {
    if (!bookId || !requestRecord?.id || !user?.uid) return;
    if (dispatchingRequests[requestRecord.id]) return;

    const leads = (externalLeads[requestRecord.id] || []).filter((lead) => !!lead.email);
    if (leads.length === 0) {
      setDiscoveryMeta((prev) => ({
        ...prev,
        [requestRecord.id]: {
          ...(prev[requestRecord.id] || requestRecord.externalDiscovery || {}),
          outboundStatus: 'no_dispatchable_leads'
        }
      }));
      return;
    }

    setDispatchingRequests((prev) => ({ ...prev, [requestRecord.id]: true }));
    try {
      const result = await requestsService.dispatchExternalRequestOutreach(
        bookId,
        requestRecord.id,
        leads.map((lead) => lead.id),
        Math.min(leads.length, 5)
      );

      setDiscoveryMeta((prev) => ({
        ...prev,
        [requestRecord.id]: {
          ...(prev[requestRecord.id] || requestRecord.externalDiscovery || {}),
          outboundStatus: result?.failed > 0 && result?.dispatched === 0 ? 'failed' : 'dispatched',
          lastDispatchSentCount: result?.dispatched || 0,
          lastDispatchFailedCount: result?.failed || 0
        }
      }));

      await fetchExternalLeads(requestRecord.id);
    } catch (error) {
      console.error('Failed to dispatch outbound outreach:', error);
      setDiscoveryMeta((prev) => ({
        ...prev,
        [requestRecord.id]: {
          ...(prev[requestRecord.id] || requestRecord.externalDiscovery || {}),
          outboundStatus: 'failed',
          outboundError: error?.message || 'Dispatch failed'
        }
      }));
    } finally {
      setDispatchingRequests((prev) => ({ ...prev, [requestRecord.id]: false }));
    }
  };

  const fetchOffersForRequest = async (requestId, requestRecord = null) => {
    if (!bookId || !requestId) return;
    
    setLoadingOffers(prev => ({ ...prev, [requestId]: true }));
    
    try {
      const offers = await requestsService.getOffersForRequest(bookId, requestId);
      setRequestOffers(prev => ({ ...prev, [requestId]: offers }));

      const mission = requestRecord || requests.find((item) => item.id === requestId);
      const discoveryStatus = mission?.externalDiscovery?.status || discoveryMeta[requestId]?.status;
      const shouldAutoDiscover =
        mission &&
        user?.uid &&
        offers.length === 0 &&
        (mission.status === 'OPEN' || mission.status === 'submitted' || mission.status === 'open') &&
        !autoDiscoveryRef.current[requestId] &&
        !['searching', 'completed', 'no_results', 'not_required'].includes(discoveryStatus);

      if (shouldAutoDiscover) {
        await triggerExternalDiscovery(mission);
      } else if (['completed', 'no_results'].includes(discoveryStatus)) {
        await fetchExternalLeads(requestId);
      }
    } catch (error) {
      console.error('Failed to fetch offers:', error);
    } finally {
      setLoadingOffers(prev => ({ ...prev, [requestId]: false }));
    }
  };

  console.log('Request Offers:', requestOffers);

  const toggleExpandOffers = (requestId, requestRecord = null) => {
    setExpandedOffers(prev => ({ ...prev, [requestId]: !prev[requestId] }));
    
    // Fetch offers if not already loaded
    if (!requestOffers[requestId] && !loadingOffers[requestId]) {
      fetchOffersForRequest(requestId, requestRecord);
    }
  };

  const handleAcceptOffer = async (request, offer) => {
    try {
      await requestsService.acceptOffer(bookId, request.id, offer.id);
      
      // Update request with accepted offer
      const updatedRequest = {
        ...request,
        status: 'IN_PROGRESS',
        acceptedOffer: {
          ...offer,
          acceptedAt: new Date().toISOString()
        }
      };
      
      await requestsService.updateRequestStatus(bookId, request.id, 'IN_PROGRESS');
      onUpdateRequest(updatedRequest);
      
      // Remove from local offers
      setRequestOffers(prev => ({
        ...prev,
        [request.id]: prev[request.id]?.filter(o => o.id !== offer.id) || []
      }));
      
      // Call parent handler if provided
      if (onAcceptOffer) {
        onAcceptOffer(offer);
      }
    } catch (error) {
      console.error('Failed to accept offer:', error);
    }
  };

  const handleDeclineOffer = async (requestId, offerId) => {
    try {
      await requestsService.updateOfferStatus(bookId, offerId, 'declined');
      setRequestOffers(prev => ({
        ...prev,
        [requestId]: prev[requestId]?.filter(o => o.id !== offerId) || []
      }));
    } catch (error) {
      console.error('Failed to decline offer:', error);
    }
  };

  const handleManageClick = (req) => {
    setSelectedRequest(req);
    setRating(5);
    setComment('');
    setIsManageModalOpen(true);
    
    // Fetch offers if not loaded
    if (!requestOffers[req.id]) {
      fetchOffersForRequest(req.id, req);
    }
  };

  const handleCancelClick = (req, e) => {
    e.stopPropagation();
    setRequestToCancel(req);
  };

  const confirmCancelRequest = async () => {
    if (!requestToCancel) return;
    
    try {
      await requestsService.updateRequestStatus(bookId, requestToCancel.id, 'CANCELLED');
      
      const updated = { ...requestToCancel, status: 'CANCELLED' };
      onUpdateRequest(updated);
      
      if (selectedRequest?.id === requestToCancel.id) {
        setSelectedRequest(updated);
      }
    } catch (error) {
      console.error('Failed to cancel request:', error);
    }
    
    setRequestToCancel(null);
  };

  const handleMarkCompleted = async () => {
    if (!selectedRequest) return;
    
    try {
      await requestsService.updateRequestStatus(bookId, selectedRequest.id, 'COMPLETED');
      const updated = { ...selectedRequest, status: 'COMPLETED' };
      onUpdateRequest(updated);
      setSelectedRequest(updated);
    } catch (error) {
      console.error('Failed to mark completed:', error);
    }
  };

  const handleSubmitFeedback = async (e) => {
    e.preventDefault();
    if (!selectedRequest) return;

    const feedback = {
      rating,
      comment,
      timestamp: new Date().toISOString()
    };

    const updated = { ...selectedRequest, feedback };
    onUpdateRequest(updated);
    setSelectedRequest(updated);
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || !selectedRequest) return;

    setUploading(true);
    
    const newDocs = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const reader = new FileReader();
      
      await new Promise((resolve) => {
        reader.onload = (event) => {
          if (event.target?.result) {
            newDocs.push(event.target.result);
          }
          resolve();
        };
        reader.readAsDataURL(file);
      });
    }

    const updatedDocs = [...(selectedRequest.completionDocs || []), ...newDocs];
    const updated = { ...selectedRequest, completionDocs: updatedDocs };
    
    onUpdateRequest(updated);
    setSelectedRequest(updated);
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'OPEN':
      case 'open':
      case 'submitted':
        return 'bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300';
      case 'IN_PROGRESS':
      case 'in-progress':
        return 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300';
      case 'COMPLETED':
      case 'closed':
        return 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300';
      case 'CANCELLED':
        return 'bg-red-100 dark:bg-red-900/50 text-red-500 dark:text-red-400';
      default:
        return 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400';
    }
  };

  const renderExternalLeadCard = (lead) => (
    <div
      key={lead.id || lead.leadId || lead.website}
      className="rounded-xl border border-amber-200 bg-amber-50/80 p-4 dark:border-amber-900/40 dark:bg-amber-900/10"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <h5 className="font-semibold text-slate-900 dark:text-white">{lead.name || 'External Provider'}</h5>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{lead.description || 'Discovered outside the registered Agent King network.'}</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className="rounded-full bg-white px-2 py-1 text-[11px] font-bold uppercase tracking-wide text-amber-700 shadow-sm dark:bg-slate-800 dark:text-amber-300">
            External
          </span>
          {typeof lead.matchScore === 'number' && (
            <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
              Score {lead.matchScore}
            </span>
          )}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-3 text-sm text-slate-600 dark:text-slate-300">
        {lead.website && (
          <a href={lead.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-indigo-600 hover:underline dark:text-indigo-400">
            <Globe className="h-4 w-4" />
            Website
          </a>
        )}
        {lead.email && (
          <span className="inline-flex items-center gap-1">
            <Mail className="h-4 w-4" />
            {lead.email}
          </span>
        )}
        {lead.phone && (
          <span className="inline-flex items-center gap-1">
            <Phone className="h-4 w-4" />
            {lead.phone}
          </span>
        )}
        {lead.location && (
          <span className="inline-flex items-center gap-1">
            <MapPin className="h-4 w-4" />
            {lead.location}
          </span>
        )}
      </div>
    </div>
  );

  const renderDiscoveryPanel = (requestRecord) => {
    const discovery = discoveryMeta[requestRecord.id] || requestRecord.externalDiscovery || {};
    const leads = externalLeads[requestRecord.id] || [];
    const isDiscovering = !!discoveringRequests[requestRecord.id];
    const isDispatching = !!dispatchingRequests[requestRecord.id];
    const dispatchableLeadCount = leads.filter((lead) => !!lead.email).length;

    return (
      <div className="mt-4 rounded-xl border border-dashed border-amber-200 bg-amber-50/60 p-4 dark:border-amber-900/40 dark:bg-amber-900/10">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <div className="text-sm font-bold uppercase tracking-wide text-amber-700 dark:text-amber-300">
              Outside-Network Discovery
            </div>
            <p className="mt-1 text-sm text-slate-700 dark:text-slate-300">
              {isDiscovering
                ? 'Agent King is searching the web for unregistered providers and preparing outbound leads.'
                : leads.length > 0
                  ? `Agent King found ${leads.length} unregistered provider${leads.length === 1 ? '' : 's'} for this mission.`
                  : discovery.status === 'no_results'
                    ? 'Agent King checked beyond the registered network but did not find a strong external provider yet.'
                    : discovery.status === 'not_required'
                      ? 'Registered providers already cover this mission, so outbound discovery was not required.'
                      : 'No provider is active on this mission yet. Agent King can expand the search beyond the registered network.'}
            </p>
            {(discovery.internalMatchCount || discovery.existingOfferCount) ? (
              <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                Internal matches: {discovery.internalMatchCount || 0} · Existing offers: {discovery.existingOfferCount || 0}
              </p>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => triggerExternalDiscovery(requestRecord, true)}
              disabled={isDiscovering || !user?.uid}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-amber-300 bg-white px-4 py-2 text-sm font-semibold text-amber-700 transition-colors hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-amber-900/50 dark:bg-slate-800 dark:text-amber-300 dark:hover:bg-slate-700"
            >
              {isDiscovering ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              {isDiscovering ? 'Searching Web' : 'Refresh External Search'}
            </button>

            <button
              onClick={() => dispatchOutboundRequests(requestRecord)}
              disabled={isDispatching || isDiscovering || dispatchableLeadCount === 0 || !user?.uid}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-emerald-300 bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60 dark:border-emerald-900/40"
            >
              {isDispatching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {isDispatching ? 'Dispatching' : 'Dispatch Outbound Requests'}
            </button>
          </div>
        </div>

        {discovery.outboundStatus && (
          <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
            Outbound status: {String(discovery.outboundStatus).replace('_', ' ')}
            {typeof discovery.lastDispatchSentCount === 'number' ? ` · Sent ${discovery.lastDispatchSentCount}` : ''}
            {typeof discovery.lastDispatchFailedCount === 'number' ? ` · Failed ${discovery.lastDispatchFailedCount}` : ''}
          </p>
        )}

        {leads.length > 0 && (
          <div className="mt-4 grid gap-3">
            {leads.map((lead) => renderExternalLeadCard(lead))}
          </div>
        )}
      </div>
    );
  };

  const renderOfferCard = (offer, request) => (
    <div 
      key={offer.id} 
      className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4 hover:shadow-md transition-shadow"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
            {offer.providerName?.charAt(0) || 'P'}
          </div>
          <div>
            <h4 className="font-bold text-slate-900 dark:text-white">{offer.providerName || 'Provider'}</h4>
            <p className="text-sm text-slate-500 dark:text-slate-400">{offer.providerType || 'Service Provider'}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400">{offer.priceEstimate || offer.price}</p>
          <p className="text-xs text-slate-500 dark:text-slate-400">{offer.timeframe || 'Flexible'}</p>
        </div>
      </div>
      
      {offer.valueProp && (
        <p className="mt-3 text-sm text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-900/50 p-3 rounded-lg">
          "{offer.valueProp}"
        </p>
      )}
      
      {offer.highlights && (
        <div className="mt-3 flex flex-wrap gap-2">
          {offer.highlights.map((h, i) => (
            <span key={i} className="px-2 py-1 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 text-xs rounded-full">
              {h}
            </span>
          ))}
        </div>
      )}
      
      <div className="mt-4 flex gap-2">
        <button
          onClick={() => handleAcceptOffer(request, offer)}
          className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors"
        >
          <Check className="w-4 h-4" />
          Accept Match
        </button>
        <button
          onClick={() => handleDeclineOffer(request.id, offer.id)}
          className="px-4 py-2.5 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 font-medium rounded-lg hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto animate-fade-in">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white">My Missions</h1>
        <p className="text-slate-500 dark:text-slate-400">Track outcomes, review agent matches, and manage fulfillment.</p>
      </div>

      {requests.length === 0 ? (
        <div className="text-center py-20 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 border-dashed">
          <div className="w-16 h-16 bg-indigo-50 dark:bg-indigo-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText className="w-8 h-8 text-indigo-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">No missions yet</h3>
          <p className="text-slate-500 dark:text-slate-400 mb-6">Describe your first outcome to activate Agent King.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {requests.map((req) => {
            const offers = requestOffers[req.id] || [];
            const isLoading = loadingOffers[req.id];
            const isExpanded = expandedOffers[req.id];
            const isOpen = req.status === 'OPEN' || req.status === 'open' || req.status === 'submitted';
            
            return (
              <div 
                key={req.id} 
                className={`bg-white dark:bg-slate-800 rounded-xl border shadow-sm overflow-hidden hover:shadow-md transition-shadow ${
                  req.status === 'CANCELLED' ? 'border-slate-100 dark:border-slate-700 opacity-75' : 'border-slate-200 dark:border-slate-700'
                }`}
              >
                <div className="p-6">
                  {/* Header */}
                  <div className="flex flex-col md:flex-row justify-between md:items-start gap-4 mb-4">
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className={`text-xl font-bold ${req.status === 'CANCELLED' ? 'text-slate-500 line-through' : 'text-slate-900 dark:text-white'}`}>
                          {req.title || req.description?.slice(0, 50) || "Outcome Mission"}
                        </h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${getStatusColor(req.status)}`}>
                          {req.status?.replace('_', ' ')}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                        <span>Posted {req.createdAt?.toDate ? req.createdAt.toDate().toLocaleDateString() : new Date(req.postedAt || req.createdAt).toLocaleDateString()}</span>
                        {req.location && (
                          <>
                            <span>•</span>
                            <span>{req.location}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {isOpen && (
                        <button
                          onClick={(e) => handleCancelClick(req, e)}
                          className="px-4 py-2 bg-white dark:bg-slate-700 border border-red-200 dark:border-red-900/50 text-red-600 dark:text-red-400 font-medium rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-sm"
                        >
                          Cancel
                        </button>
                      )}
                      <button 
                        onClick={() => handleManageClick(req)}
                        className="px-4 py-2 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 font-medium rounded-lg hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors text-sm"
                      >
                        Manage
                      </button>
                    </div>
                  </div>

                  {/* Description */}
                  <div className="bg-slate-50 dark:bg-slate-900/50 rounded-lg p-4 mb-4">
                    <p className="text-slate-700 dark:text-slate-300 mb-3">{req.description}</p>
                    <div className="flex flex-wrap gap-4 text-sm text-slate-600 dark:text-slate-400">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {req.timeframe === 'asap' ? 'ASAP' : req.timeframe}
                      </span>
                      {req.maxResponses && (
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          Max {req.maxResponses} Matches
                        </span>
                      )}
                      {req.category && (
                        <span className="flex items-center gap-1 bg-indigo-50 dark:bg-indigo-900/30 px-2 py-0.5 rounded text-indigo-600 dark:text-indigo-400 font-medium text-xs">
                          {req.category}
                        </span>
                      )}
                      {req.attachments?.length > 0 && (
                        <span className="flex items-center gap-1 text-indigo-600 dark:text-indigo-400 font-medium">
                          <Paperclip className="w-4 h-4" />
                          {req.attachments.length} Attachment(s)
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Offers Section for Open Requests */}
                  {isOpen && (
                    <div className="border-t border-slate-100 dark:border-slate-700 pt-4">
                      <button
                        onClick={() => toggleExpandOffers(req.id, req)}
                        className="w-full flex items-center justify-between py-3 text-left"
                      >
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-5 h-5 text-emerald-500" />
                          <span className="font-semibold text-slate-900 dark:text-white">
                            Agent Matches
                          </span>
                          {offers.length > 0 && (
                            <span className="px-2 py-0.5 bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300 text-xs font-bold rounded-full">
                              {offers.length}
                            </span>
                          )}
                        </div>
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-slate-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-slate-400" />
                        )}
                      </button>
                      
                      {isExpanded && (
                        <div className="mt-2 space-y-3">
                          {isLoading ? (
                            <div className="flex items-center justify-center py-8">
                              <Loader2 className="w-6 h-6 text-indigo-500 animate-spin" />
                              <span className="ml-2 text-slate-500">Loading matches...</span>
                            </div>
                          ) : offers.length > 0 ? (
                            offers.map(offer => renderOfferCard(offer, req))
                          ) : (
                            <div className="text-center py-8 bg-slate-50 dark:bg-slate-900/50 rounded-xl">
                              <DollarSign className="w-10 h-10 text-slate-300 dark:text-slate-600 mx-auto mb-2" />
                              <p className="text-slate-500 dark:text-slate-400">No matches yet</p>
                              <p className="text-sm text-slate-400 dark:text-slate-500">The request and offer agents are still working this mission</p>
                            </div>
                          )}

                          {offers.length === 0 && renderDiscoveryPanel(req)}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Accepted Offer Preview */}
                  {req.acceptedOffer && (
                    <div className="border-t border-slate-100 dark:border-slate-700 pt-4">
                      <h4 className="text-sm font-bold text-slate-900 dark:text-white mb-3 uppercase tracking-wide">Fulfillment Details</h4>
                      <div className="flex items-center justify-between bg-emerald-50 dark:bg-emerald-900/20 p-4 rounded-xl border border-emerald-100 dark:border-emerald-900/30">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-emerald-200 dark:bg-emerald-800 rounded-full flex items-center justify-center text-emerald-800 dark:text-emerald-200 font-bold">
                            {req.acceptedOffer.providerName?.charAt(0) || 'P'}
                          </div>
                          <div>
                            <p className="font-bold text-slate-900 dark:text-white">{req.acceptedOffer.providerName}</p>
                            <p className="text-xs text-slate-600 dark:text-slate-300">{req.acceptedOffer.providerType}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-emerald-700 dark:text-emerald-400">{req.acceptedOffer.priceEstimate}</p>
                          <p className="text-xs text-emerald-600 dark:text-emerald-500">Agreed Price</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      {requestToCancel && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-slate-200 dark:border-slate-700">
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <X className="w-8 h-8 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Cancel Mission?</h3>
              <p className="text-slate-600 dark:text-slate-300 mb-6">
                Are you sure you want to cancel "<span className="font-semibold">{requestToCancel.title || 'this mission'}</span>"?
              </p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => setRequestToCancel(null)}
                  className="px-5 py-2.5 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 font-semibold rounded-lg hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors"
                >
                  Keep Mission
                </button>
                <button
                  onClick={confirmCancelRequest}
                  className="px-5 py-2.5 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors"
                >
                  Yes, Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manage Request Modal */}
      {isManageModalOpen && selectedRequest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-slate-200 dark:border-slate-700">
            <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center sticky top-0 bg-white dark:bg-slate-800 z-10">
              <h3 className="font-bold text-lg text-slate-900 dark:text-white">Manage Mission</h3>
              <button onClick={() => setIsManageModalOpen(false)} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-8">
              {/* Status Section */}
              <section>
                <h4 className="text-sm font-bold text-slate-900 dark:text-white uppercase mb-4">Status & Progress</h4>
                <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-900/50 p-4 rounded-xl border border-slate-100 dark:border-slate-700">
                  <div>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">Current Status</p>
                    <p className={`font-bold text-lg ${
                      selectedRequest.status === 'OPEN' || selectedRequest.status === 'open' || selectedRequest.status === 'submitted' ? 'text-indigo-600 dark:text-indigo-400' :
                      selectedRequest.status === 'IN_PROGRESS' ? 'text-blue-600 dark:text-blue-400' :
                      selectedRequest.status === 'COMPLETED' ? 'text-emerald-600 dark:text-emerald-400' : 
                      'text-red-600 dark:text-red-400'
                    }`}>
                      {selectedRequest.status?.replace('_', ' ')}
                    </p>
                  </div>
                  {selectedRequest.status === 'IN_PROGRESS' && (
                    <button 
                      onClick={handleMarkCompleted}
                      className="px-4 py-2 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors"
                    >
                      Mark as Completed
                    </button>
                  )}
                </div>
              </section>

              {/* Offers in Modal for OPEN requests */}
              {(selectedRequest.status === 'OPEN' || selectedRequest.status === 'open' || selectedRequest.status === 'submitted') && (
                <section>
                  <h4 className="text-sm font-bold text-slate-900 dark:text-white uppercase mb-4">
                    Pending Matches ({requestOffers[selectedRequest.id]?.length || 0})
                  </h4>
                  <div className="space-y-3">
                    {loadingOffers[selectedRequest.id] ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 text-indigo-500 animate-spin" />
                      </div>
                    ) : requestOffers[selectedRequest.id]?.length > 0 ? (
                      requestOffers[selectedRequest.id].map(offer => renderOfferCard(offer, selectedRequest))
                    ) : (
                      <div className="text-center py-8 bg-slate-50 dark:bg-slate-900/50 rounded-xl">
                        <p className="text-slate-500">No matches yet</p>
                      </div>
                    )}

                    {(requestOffers[selectedRequest.id]?.length || 0) === 0 && renderDiscoveryPanel(selectedRequest)}
                  </div>
                </section>
              )}

              {/* Accepted Offer Details */}
              {selectedRequest.acceptedOffer && (
                <section>
                  <h4 className="text-sm font-bold text-slate-900 dark:text-white uppercase mb-4">Matched Provider</h4>
                  <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h5 className="font-bold text-lg text-slate-800 dark:text-white">{selectedRequest.acceptedOffer.offerTitle || selectedRequest.acceptedOffer.providerName}</h5>
                        <p className="text-slate-600 dark:text-slate-300 text-sm mb-2">{selectedRequest.acceptedOffer.providerType}</p>
                      </div>
                      <span className="font-mono font-bold text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded">
                        {selectedRequest.acceptedOffer.priceEstimate}
                      </span>
                    </div>
                    <div className="mt-4 flex gap-3">
                      <button className="flex-1 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 flex items-center justify-center gap-2">
                        <MessageSquare className="w-4 h-4" /> Message
                      </button>
                      <button className="flex-1 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 flex items-center justify-center gap-2">
                        <Phone className="w-4 h-4" /> Call
                      </button>
                    </div>
                  </div>
                </section>
              )}

              {/* Completion & Feedback */}
              {(selectedRequest.status === 'COMPLETED' || selectedRequest.status === 'IN_PROGRESS') && (
                <>
                  <section>
                    <h4 className="text-sm font-bold text-slate-900 dark:text-white uppercase mb-4">Documents & Evidence</h4>
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      {selectedRequest.completionDocs?.map((doc, i) => (
                        <div key={i} className="aspect-square bg-slate-100 dark:bg-slate-700 rounded-lg overflow-hidden border border-slate-200 dark:border-slate-600">
                          <img src={doc} alt="Doc" className="w-full h-full object-cover" />
                        </div>
                      ))}
                      <button 
                        onClick={() => fileInputRef.current?.click()}
                        className="aspect-square bg-slate-50 dark:bg-slate-900/50 rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-600 flex flex-col items-center justify-center text-slate-400 hover:border-indigo-500 hover:text-indigo-500 transition-colors cursor-pointer"
                      >
                        {uploading ? (
                          <Loader2 className="w-6 h-6 animate-spin" />
                        ) : (
                          <>
                            <Upload className="w-8 h-8 mb-1" />
                            <span className="text-xs font-bold">Upload</span>
                          </>
                        )}
                      </button>
                      <input 
                        type="file" 
                        ref={fileInputRef} 
                        onChange={handleFileUpload} 
                        className="hidden" 
                        multiple 
                        accept="image/*,.pdf" 
                      />
                    </div>
                  </section>

                  {selectedRequest.status === 'COMPLETED' && (
                    <section className="pt-6 border-t border-slate-100 dark:border-slate-700">
                      <h4 className="text-sm font-bold text-slate-900 dark:text-white uppercase mb-4">Feedback</h4>
                      {selectedRequest.feedback ? (
                        <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-xl border border-yellow-100 dark:border-yellow-900/30">
                          <div className="flex items-center gap-1 text-yellow-500 mb-2">
                            {[...Array(5)].map((_, i) => (
                              <Star key={i} className={`w-5 h-5 ${i < selectedRequest.feedback.rating ? 'fill-current' : 'text-yellow-200 dark:text-yellow-900'}`} />
                            ))}
                          </div>
                          <p className="text-slate-700 dark:text-slate-300 italic">"{selectedRequest.feedback.comment}"</p>
                        </div>
                      ) : (
                        <form onSubmit={handleSubmitFeedback} className="bg-slate-50 dark:bg-slate-900/50 p-6 rounded-xl border border-slate-200 dark:border-slate-700">
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Rate Provider</label>
                            <div className="flex gap-2">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <button
                                  key={star}
                                  type="button"
                                  onClick={() => setRating(star)}
                                  className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                                    rating >= star ? 'bg-yellow-400 text-white' : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 text-slate-300'
                                  }`}
                                >
                                  <Star className="w-6 h-6 fill-current" />
                                </button>
                              ))}
                            </div>
                          </div>
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Comment</label>
                            <textarea 
                              value={comment}
                              onChange={(e) => setComment(e.target.value)}
                              className="w-full p-3 border border-slate-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                              rows={3}
                              placeholder="How was the service?"
                              required
                            />
                          </div>
                          <button type="submit" className="w-full py-3 bg-indigo-600 text-white font-bold rounded-lg hover:bg-indigo-700 transition-colors">
                            Submit Review
                          </button>
                        </form>
                      )}
                    </section>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDashboard;
